import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import { getPublicStats, getPublicDives, getPublicDiveStamps } from '../api';

function StatCard({ label, value, sub }) {
  return (
    <div style={{
      background: 'var(--card)',
      borderRadius: 12,
      padding: '20px 24px',
      border: '1px solid var(--border)',
      flex: '1 1 140px',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .8, color: 'var(--text-muted)', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--blue)', lineHeight: 1 }}>
        {value ?? '–'}
      </div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function fmtDuration(sec) {
  if (!sec) return '–';
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}u ${m}min` : `${m}min`;
}

function FitBounds({ locations }) {
  const { useMap } = require('react-leaflet');
  const map = useMap();
  useEffect(() => {
    if (locations?.length > 0) {
      const lats = locations.map(l => l.latitude);
      const lons = locations.map(l => l.longitude);
      map.fitBounds([
        [Math.min(...lats), Math.min(...lons)],
        [Math.max(...lats), Math.max(...lons)],
      ], { padding: [30, 30] });
    }
  }, [locations, map]);
  return null;
}

export default function PublicLogbook() {
  const { token } = useParams();
  const [stats, setStats] = useState(null);
  const [dives, setDives] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stampModal, setStampModal] = useState(null);  // { dive, stamps }
  const [loadingStamps, setLoadingStamps] = useState(false);

  useEffect(() => {
    Promise.all([getPublicStats(token), getPublicDives(token)])
      .then(([s, d]) => { setStats(s); setDives(d); })
      .catch(err => setError(err.response?.data?.error || 'Ongeldig of verlopen link'))
      .finally(() => setLoading(false));
  }, [token]);

  const openStamps = async (dive) => {
    setLoadingStamps(true);
    try {
      const stamps = await getPublicDiveStamps(token, dive.id);
      setStampModal({ dive, stamps });
    } catch {}
    setLoadingStamps(false);
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface)' }}>
      <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🤿</div>
        <div>Laden…</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface)', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Geen toegang</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>{error}</div>
      </div>
    </div>
  );

  const { totals, byYear, locations } = stats;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', padding: '0 0 60px' }}>

      {/* Header */}
      <div style={{ background: 'var(--blue)', color: '#fff', padding: '32px 24px 28px', textAlign: 'center' }}>
        <div style={{ fontSize: 42, marginBottom: 8 }}>🤿</div>
        <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: 1 }}>DIVE LOGBOOK</div>
        <div style={{ opacity: .75, fontSize: 14, marginTop: 4 }}>
          {totals.first_dive
            ? `${new Date(totals.first_dive).getFullYear()} – ${new Date(totals.last_dive).getFullYear()}`
            : ''}
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>

        {/* Stat cards */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
          <StatCard label="Duiken" value={totals.total_dives} />
          <StatCard label="Uren" value={totals.total_hours} sub="onder water" />
          <StatCard label="Diepste duik" value={totals.deepest_dive ? `${totals.deepest_dive} m` : '–'} />
          <StatCard label="Gem. diepte" value={totals.avg_max_depth ? `${totals.avg_max_depth} m` : '–'} />
          <StatCard label="Koudste temp." value={totals.coldest_temp != null ? `${totals.coldest_temp}°C` : '–'} />
          <StatCard label="Jaren actief" value={totals.years_diving} />
        </div>

        {/* Kaart */}
        {locations?.length > 0 && (
          <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)', marginBottom: 24, height: 320 }}>
            <MapContainer
              center={[20, 0]}
              zoom={2}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
              dragging={true}
              zoomControl={true}
              attributionControl={false}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FitBounds locations={locations} />
              {locations.map((loc, i) => (
                <CircleMarker
                  key={i}
                  center={[loc.latitude, loc.longitude]}
                  radius={6}
                  pathOptions={{ color: '#fff', weight: 2, fillColor: '#2563eb', fillOpacity: 0.85 }}
                >
                  <Popup>
                    <strong>{loc.location_name || 'Onbekende locatie'}</strong><br />
                    {new Date(loc.dive_date).toLocaleDateString('nl')}<br />
                    {loc.max_depth_m} m diep
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        )}

        {/* Per jaar */}
        {byYear?.length > 0 && (
          <div style={{ background: 'var(--card)', borderRadius: 12, border: '1px solid var(--border)', padding: '20px 24px', marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .8, color: 'var(--text-muted)', marginBottom: 16 }}>
              Duiken per jaar
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {byYear.map(y => (
                <div key={y.year} style={{ textAlign: 'center', padding: '10px 20px', background: 'var(--surface)', borderRadius: 10 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--blue)' }}>{y.dives}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{y.year}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Duikenlijst */}
        <div style={{ background: 'var(--card)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .8, color: 'var(--text-muted)' }}>
            Alle duiken ({dives.length})
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['#', 'Datum', 'Locatie', 'Diepte', 'Duur', 'Temp.', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: .8, color: 'var(--text-muted)', fontWeight: 700, padding: '10px 14px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dives.map((dive, idx) => (
                  <tr key={dive.id} style={{ borderBottom: '1px solid var(--border)', background: idx % 2 === 0 ? 'transparent' : 'var(--surface)' }}>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontWeight: 600 }}>
                      {dive.dive_number ?? (dives.length - idx)}
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {new Date(dive.dive_date).toLocaleDateString('nl', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td style={{ padding: '10px 14px', maxWidth: 200 }}>
                      {dive.location_name || dive.site_name || <span style={{ color: 'var(--text-muted)' }}>–</span>}
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {dive.max_depth_m != null ? `${dive.max_depth_m} m` : '–'}
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {fmtDuration(dive.duration_sec)}
                    </td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: dive.temp_min_c != null && dive.temp_min_c < 15 ? '#2563eb' : 'inherit' }}>
                      {dive.temp_min_c != null ? `${dive.temp_min_c}°C` : '–'}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      {dive.stamp_count > 0 && (
                        <button
                          onClick={() => openStamps(dive)}
                          disabled={loadingStamps}
                          title={`${dive.stamp_count} stempel${dive.stamp_count > 1 ? 's' : ''} bekijken`}
                          style={{
                            background: 'none', border: '1px solid var(--border)', borderRadius: 99,
                            padding: '3px 8px', fontSize: 11, cursor: 'pointer',
                            color: 'var(--text-muted)', whiteSpace: 'nowrap',
                            display: 'flex', alignItems: 'center', gap: 4,
                          }}
                        >
                          🔏 {dive.stamp_count}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 32, color: 'var(--text-muted)', fontSize: 12 }}>
          🤿 Dive Logbook — persoonlijk duiklogboek
        </div>
      </div>

      {/* Stempel modal */}
      {stampModal && (
        <div
          onClick={() => setStampModal(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,.85)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--card)', borderRadius: 16, padding: 24,
              maxWidth: 540, width: '100%', maxHeight: '80vh', overflowY: 'auto',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
              🔏 Stempels & handtekeningen
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
              {stampModal.dive.location_name || stampModal.dive.site_name || 'Onbekende locatie'} ·{' '}
              {new Date(stampModal.dive.dive_date).toLocaleDateString('nl', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
              {stampModal.stamps.map(stamp => (
                <div key={stamp.id} style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
                  <img
                    src={`/uploads/stamps/${stamp.filename}.jpg`}
                    alt={stamp.dive_center || 'Stempel'}
                    style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }}
                  />
                  {stamp.dive_center && (
                    <div style={{ padding: '5px 8px', fontSize: 11, color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>
                      ✍️ {stamp.dive_center}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={() => setStampModal(null)}
              className="btn btn-outline"
              style={{ marginTop: 16, width: '100%', fontSize: 13 }}
            >
              Sluiten
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
